// The quota editor (SCR-230): create or edit one cap in a dialog — what it applies to (with the
// team or person from the org's own lists), what it measures, the period, the limit (dollars for
// spend, sent as integer µUSD), warnings, enforcement, grace and the fail mode — with the preview
// (REQ-094) under the form. An edit changes only what a quota may change (its scope, measure,
// basis, period and owner are its identity) and carries the version it read; a version conflict
// says so and offers a reload.
import { useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { useReadOnly } from "../../host/readonly";
import { Dialog } from "../../primitives/dialog";
import { Field, InputAffix } from "../../primitives/field";
import { fieldErrorsFrom } from "../../primitives/forms";
import { ErrorDetails } from "../../primitives/states";
import { PreviewPanel } from "./preview-panel";
import {
  amountInput,
  asApiError,
  blankQuotaForm,
  canPreview,
  createQuota,
  formFromQuota,
  isVersionConflict,
  METRICS,
  metricLabel,
  patchChanges,
  patchQuota,
  PERIODS,
  periodLabel,
  previewDraft,
  SCOPES,
  scopeLabel,
  scopeTarget,
  toDraft,
  toPatch,
  validateQuotaForm,
  writeFailure,
  type EditorMode,
  type Quota,
  type QuotaFormValues,
  type Targets,
  type WriteAccess,
} from "./model";
import { personName } from "./names";

export interface QuotaEditorProps {
  context: OrgContext;
  access: WriteAccess;
  mode: EditorMode;
  targets: Targets;
  open: boolean;
  onClose(): void;
  onSaved(quota: Quota): void;
  /** Reload the quota after a version conflict. */
  onReload(): void;
}

export function QuotaEditor({ context, access, mode, targets, open, onClose, onSaved, onReload }: QuotaEditorProps) {
  const client = useApi();
  const { readOnly, reason } = useReadOnly();
  const [values, setValues] = useState<QuotaFormValues>(() => (mode.kind === "edit" ? formFromQuota(mode.quota) : blankQuotaForm(access)));
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [touched, setTouched] = useState(false);

  const editing = mode.kind === "edit";
  const staff = context.surface === "staff";
  const clientErrors = validateQuotaForm(values, { mode, surface: context.surface, access });
  // A 422's field details show beside their fields until the next edit.
  const errors = { ...clientErrors, ...(failure ? fieldErrorsFrom(failure) : {}) };
  const shown = (field: string) => (touched ? errors[field] : undefined);
  const patch = mode.kind === "edit" ? toPatch(values, mode.quota) : null;
  const unchanged = patch !== null && !patchChanges(patch);
  const draft = canPreview(values, clientErrors) ? previewDraft(values, mode) : null;
  const target = scopeTarget(values.scope);

  const set = <K extends keyof QuotaFormValues>(key: K, value: QuotaFormValues[K]) => {
    setFailure(null);
    setValues((current) => ({ ...current, [key]: value }));
  };
  const setMetric = (metric: QuotaFormValues["metric"]) => {
    setFailure(null);
    setValues((current) => ({ ...current, metric, limit: "" }));
  };

  const save = () => {
    setTouched(true);
    if (Object.keys(clientErrors).length > 0 || saving || unchanged) return;
    setSaving(true);
    setFailure(null);
    const write = mode.kind === "edit" && patch ? patchQuota(client, context.orgId, mode.quota.id, patch) : createQuota(client, context.orgId, toDraft(values));
    write
      .then(onSaved)
      .catch((error: unknown) => setFailure(asApiError(error)))
      .finally(() => setSaving(false));
  };

  const limitHint =
    mode.kind === "edit" && mode.quota.limit === null ? "The current limit is hidden from you; leave this empty to keep it." : values.metric === "spend" ? "In dollars." : undefined;

  return (
    <Dialog
      title={editing ? `Edit ${mode.quota.name}` : "New quota"}
      open={open}
      onClose={onClose}
      class="dialog-wide quota-editor"
      closeLabel="Close the editor"
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" class="button button-primary" disabled={readOnly || saving || unchanged} title={readOnly ? reason ?? "" : undefined} aria-describedby={readOnly ? "quotas-stale-note" : undefined} onClick={save}>
            {saving ? "Saving…" : editing ? "Save changes" : "Create quota"}
          </button>
        </>
      }
    >
      <form
        class="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        {failure && (
          <div class={isVersionConflict(failure) ? "banner banner-warning" : "banner banner-error"} role="alert">
            <p>{writeFailure(failure)}</p>
            <ErrorDetails error={failure} />
            {isVersionConflict(failure) && (
              <button type="button" class="button" onClick={onReload}>
                Reload
              </button>
            )}
          </div>
        )}
        <div class="form-grid">
          <Field id="quota-name" label="Name" help="quotas.name" error={shown("name")}>
            <input id="quota-name" type="text" maxLength={100} value={values.name} onInput={(event) => set("name", event.currentTarget.value)} />
          </Field>
          <Field id="quota-scope" label="Applies to" help="quotas.scope" hint={editing ? "A quota's scope cannot change; create another." : undefined}>
            <select id="quota-scope" value={values.scope} disabled={editing} onChange={(event) => set("scope", event.currentTarget.value as QuotaFormValues["scope"])}>
              {SCOPES.map((scope) => (
                <option key={scope} value={scope}>
                  {scopeLabel(scope)}
                </option>
              ))}
            </select>
          </Field>
          {target === "team" && (
            <Field id="quota-team" label="Team" help="quotas.target" error={shown("teamId")}>
              <select id="quota-team" value={values.teamId} disabled={editing} onChange={(event) => set("teamId", event.currentTarget.value)}>
                <option value="">Pick a team…</option>
                {targets.teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {target === "user" && (
            <Field id="quota-user" label="Person" help="quotas.target" error={shown("userId")}>
              <select id="quota-user" value={values.userId} disabled={editing} onChange={(event) => set("userId", event.currentTarget.value)}>
                <option value="">Pick a person…</option>
                {targets.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {personName(user)}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field id="quota-metric" label="Measure" help="quotas.metric">
            <select id="quota-metric" value={values.metric} disabled={editing} onChange={(event) => setMetric(event.currentTarget.value as QuotaFormValues["metric"])}>
              {METRICS.map((metric) => (
                <option key={metric} value={metric}>
                  {metricLabel(metric)}
                </option>
              ))}
            </select>
          </Field>
          {values.metric === "spend" && (
            <Field id="quota-basis" label="Spend basis" help="quotas.basis" error={shown("basis")}>
              <select id="quota-basis" value={values.basis} disabled={editing} onChange={(event) => set("basis", event.currentTarget.value as QuotaFormValues["basis"])}>
                <option value="price">Organization's price</option>
                {(staff || values.basis === "list") && <option value="list">AWS list cost (guardrails)</option>}
              </select>
            </Field>
          )}
          <Field id="quota-period" label="Period" help="quotas.period">
            <select id="quota-period" value={values.period} disabled={editing} onChange={(event) => set("period", event.currentTarget.value as QuotaFormValues["period"])}>
              {PERIODS.map((period) => (
                <option key={period} value={period}>
                  {periodLabel(period)} (UTC)
                </option>
              ))}
            </select>
          </Field>
          <Field id="quota-limit" label="Limit" help="quotas.limit" hint={limitHint} error={shown("limit")}>
            <InputAffix {...(values.metric === "spend" ? { prefix: "$" } : { suffix: values.metric })}>
              <input
                id="quota-limit"
                type="text"
                inputMode="decimal"
                placeholder={mode.kind === "edit" ? amountInput(mode.quota.metric, mode.quota.limit) : ""}
                value={values.limit}
                onInput={(event) => set("limit", event.currentTarget.value)}
              />
            </InputAffix>
          </Field>
          <Field id="quota-thresholds" label="Warn at" help="quotas.thresholds" hint="Percentages, rising, up to five." error={shown("thresholds")}>
            <InputAffix suffix="%">
              <input id="quota-thresholds" type="text" value={values.thresholds} onInput={(event) => set("thresholds", event.currentTarget.value)} />
            </InputAffix>
          </Field>
          <Field id="quota-enforcement" label="When reached" help="quotas.enforcement">
            <select id="quota-enforcement" value={values.enforcement} onChange={(event) => set("enforcement", event.currentTarget.value as QuotaFormValues["enforcement"])}>
              <option value="block">Block requests</option>
              <option value="warn">Warn only</option>
            </select>
          </Field>
          <Field id="quota-grace" label="Grace" help="quotas.grace" error={shown("grace")}>
            <InputAffix suffix="%">
              <input id="quota-grace" type="text" inputMode="decimal" value={values.grace} onInput={(event) => set("grace", event.currentTarget.value)} />
            </InputAffix>
          </Field>
          <Field id="quota-fail-mode" label="If the proxy cannot check" help="quotas.failMode">
            <select id="quota-fail-mode" value={values.failMode} onChange={(event) => set("failMode", event.currentTarget.value as QuotaFormValues["failMode"])}>
              <option value="open">Admit requests</option>
              <option value="closed">Refuse requests</option>
            </select>
          </Field>
          {staff && (
            <Field id="quota-owner" label="Owner" help="quotas.owner" error={shown("owner")}>
              <select id="quota-owner" value={values.owner} disabled={editing} onChange={(event) => set("owner", event.currentTarget.value as QuotaFormValues["owner"])}>
                {(access.org || values.owner === "org") && <option value="org">Organization</option>}
                {(access.platform || values.owner === "platform") && <option value="platform">AllCode guardrail</option>}
              </select>
            </Field>
          )}
        </div>
        {unchanged && <p class="field-hint">Nothing to save yet.</p>}
      </form>
      <PreviewPanel orgId={context.orgId} draft={draft} policy={context.moneyPolicy} />
    </Dialog>
  );
}
